import classNames from "classnames";
import MarkdownRenderer from "mt-components/MarkdownRenderer/MarkdownRenderer";
import { GetStaticPaths, GetStaticProps } from "next";
import { CloudinaryImage } from "../../../components/Atoms/Cloudinary";
import Video from "../../../components/Atoms/Cloudinary/Video";
import HeaderBanner from "../../../components/Cards/HeaderBanner";
import ProductCardList from "../../../components/Cards/ProductCardList";
import SearchBar from "../../../components/Cards/SearchBar";
import {
  addApolloState,
  initializeApollo,
} from "../../../graphql/apolloClient";
import {
  Currency,
  GetProductCollectionDocument,
  GetProductCollectionQuery,
  GetProductCollectionQueryResult,
  GetProductCollectionQueryVariables,
  GetProductCollectionsQuery,
  GetProductCollectionsQueryVariables,
  Iso639,
} from "../../../graphql/operations";
import { addDuration, formatDate } from "../../../utils/calendarUtils";
import useWindowSize from "../../../utils/useWindowSize";
import s from "./Collection.module.css";

type ProductCollectionProps = {
  queryResult?: GetProductCollectionQueryResult["data"];
};

export default function ProductCollection({
  queryResult,
}: ProductCollectionProps) {
  const collection = queryResult?.productCollection;
  const products = collection?.products.records ?? [];

  const firstLocationId =
    collection.locations[collection.locations.length - 1]?.id;
  const tagIds = collection.tags.map(({ id }) => id);

  const { width } = useWindowSize();

  if (!collection) return <></>;

  return (
    <>
      <HeaderBanner
        backdrop={
          <CloudinaryImage
            src={collection.imageUrl}
            isPriority={true}
            transformations={["w_1024", "h_460", "c_fill"]}
          />
        }
      >
        <h1 className={classNames(s.MarkdownText, s.ShadowedText)}>
          {collection.headline}
        </h1>
        <h4 className={classNames(s.MarkdownText, s.ShadowedText)}>
          <MarkdownRenderer>{collection.description}</MarkdownRenderer>
        </h4>
      </HeaderBanner>
      <div className="AppLayout">
        <h1 className={s.ListTitle}>
          {queryResult?.productCollection.listTitle}
        </h1>
        <h4 style={{ marginBottom: 32 }}>
          {queryResult?.productCollection.listDescription}
        </h4>
        <ProductCardList products={products} />
        {queryResult?.productCollection && (
          <div
            className={classNames(
              s.MarkdownContainer,
              s.MarkdownText,
              s.Content
            )}
          >
            <MarkdownRenderer
              CustomVideoComponent={(url) => (
                <Video
                  src={{
                    url,
                  }}
                  transformations={["f_auto", "q_auto", "vc_auto"]}
                />
              )}
            >
              {queryResult.productCollection.content}
            </MarkdownRenderer>
          </div>
        )}

        <div style={{ marginTop: 64, marginBottom: 15 }}>
          <SearchBar
            locationId={firstLocationId}
            tags={tagIds}
            reference="product-collection"
          />
        </div>
      </div>
    </>
  );
}

type ProductCollectionStaticParams = {
  slug: string;
};

export const getStaticProps: GetStaticProps<
  ProductCollectionProps,
  ProductCollectionStaticParams
> = async (context) => {
  const apolloClient = initializeApollo();

  const tomorrow = addDuration(new Date(), { days: 1 });
  const until = addDuration(tomorrow, { days: 1 });

  const result = await apolloClient.query<
    GetProductCollectionQuery,
    GetProductCollectionQueryVariables
  >({
    query: GetProductCollectionDocument,
    variables: {
      slug: context.params.slug,
      fromDate: formatDate(tomorrow),
      untilDate: formatDate(until),
      currency: Currency.Usd,
      lang: Iso639.En,
    },
  });

  return addApolloState(apolloClient, {
    props: {
      queryResult: result.data,
    } as ProductCollectionProps,
    revalidate: process.env.ENVIRONMENT == "production" ? 3600 : 60,
  });
};

export const getStaticPaths: GetStaticPaths = async () => {
  let paths = [];
  try {
    const apollo = initializeApollo();

    const result = await apollo.query<
      GetProductCollectionsQuery,
      GetProductCollectionsQueryVariables
    >({
      query: GetProductCollectionDocument,
    });

    paths = result.data.productCollections.records.map(
      (collection) => collection.slug
    );
  } finally {
    return { paths, fallback: "blocking" };
  }
};
